<template>
  <form
    class="v3-form behaviour-award-points-form"
    data-testid="behaviour-award-points-form"
    @submit.prevent="onSubmit"
  >
    <InfoBox
      v-if="state.serverMessage"
      :class="{ danger: state.isServerError }"
      :title="state.serverTitle"
      :message="state.serverMessage"
      announce-assertive
    />

    <div class="row mb-3 g-3">
      <div class="col-12 col-md-6">
        <Label
          required
          label-id="behaviour-award-core-value-lbl"
          html-for="behaviour-award-core-value-input"
        >
          {{ behaviourTerminology.coreValue }}
        </Label>
        <BehaviourCoreValueLazySelect
          :model-value="state.form.coreValueId"
          class="w-100"
          hide-floating-label
          labelled-by="behaviour-award-core-value-lbl"
          status-filter="active"
          :prefetch-on-mount="false"
          :placeholder="
            $t('behaviour_settings.positive_behaviour_core_value_placeholder', {
              coreValue: behaviourTerminology.coreValue
            })
          "
          :selected-option="state.coreValueSelectedOption"
          :invalid-feedback="errors.coreValueId"
          input-id="behaviour-award-core-value-input"
          append-dropdown-to-body
          append-dropdown-fixed-position
          data-testid="behaviour-award-core-value-select"
          @update:model-value="(v) => patchForm('coreValueId', v)"
          @option-selected="onCoreValueSelected"
        />
      </div>
      <div class="col-12 col-md-6">
        <Label
          required
          label-id="behaviour-award-positive-lbl"
          html-for="behaviour-award-positive-input"
        >
          {{ fieldLabels.positiveBehaviour }}
        </Label>
        <BehaviourPositiveBehaviourLazySelect
          :model-value="state.form.positiveBehaviourId"
          :core-value-id="state.form.coreValueId"
          :core-value-label="behaviourTerminology.coreValue"
          class="w-100"
          hide-floating-label
          labelled-by="behaviour-award-positive-lbl"
          status-filter="active"
          :prefetch-on-mount="false"
          :placeholder="positiveBehaviourPlaceholder"
          :selected-option="state.positiveBehaviourSelectedOption"
          :invalid-feedback="errors.positiveBehaviourId"
          input-id="behaviour-award-positive-input"
          append-dropdown-to-body
          append-dropdown-fixed-position
          data-testid="behaviour-award-positive-select"
          @update:model-value="(v) => patchForm('positiveBehaviourId', v)"
          @option-selected="onPositiveBehaviourSelected"
        />
      </div>
    </div>

    <div class="mb-3">
      <Label
        label-id="behaviour-award-description-lbl"
        html-for="behaviour-award-description-input"
      >
        {{
          $t("behaviour_settings.positive_behaviour_award_description_label")
        }}
      </Label>
      <InputField
        id="behaviour-award-description-input"
        label-id="behaviour-award-description-lbl"
        class="w-100"
        type="textarea"
        :rows="4"
        :model-value="state.form.awardDescription"
        :invalid-feedback="errors.awardDescription"
        data-testid="behaviour-award-description-input"
        @update:model-value="(v) => patchForm('awardDescription', v)"
      />
    </div>

    <BehaviourSelectStudentsFieldset
      v-model="state.form.studentIds"
      class="mb-3"
      :invalid-feedback="errors.studentIds"
      label-id="behaviour-award-students-lbl"
      input-id="behaviour-award-students-input"
      data-testid="behaviour-award-students-select"
    />

    <div class="text-center">
      <LoadingButton
        class="me-2 px-4"
        type="button"
        rounded
        data-testid="behaviour-award-cancel"
        @click="onCancel"
      >
        {{ $t("common.cancel") }}
      </LoadingButton>
      <LoadingButton
        class="px-4"
        type="submit"
        solid
        rounded
        :is-loading="state.isProcessing"
        data-testid="behaviour-award-submit"
      >
        {{ $t("behaviour_manage.award_submit") }}
      </LoadingButton>
    </div>
  </form>
</template>

<script>
import BehaviourSelectStudentsFieldset from "@/v3components/Forms/Behaviour/BehaviourSelectStudentsFieldset.vue"
import BehaviourCoreValueLazySelect from "@/v3components/Behaviour/BehaviourCoreValueLazySelect.vue"
import BehaviourPositiveBehaviourLazySelect from "@/v3components/Behaviour/BehaviourPositiveBehaviourLazySelect.vue"
import {
  normalizeBehaviourStudentIds,
  parseBehaviourPointsApiError
} from "@/helpers/behaviourPointsForm.js"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import { computed, inject, reactive } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "BehaviourAwardPointsForm",
  components: {
    BehaviourSelectStudentsFieldset,
    BehaviourCoreValueLazySelect,
    BehaviourPositiveBehaviourLazySelect,
    InfoBox,
    InputField,
    Label,
    LoadingButton
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const fieldLabels = computed(() => ({
      positiveBehaviour: behaviourTerminology.value.positiveBehaviours
    }))

    const positiveBehaviourPlaceholder = computed(() => {
      if (!state.form.coreValueId) {
        return t("behaviour_manage.select_core_value_first_placeholder", {
          coreValue: behaviourTerminology.value.coreValue
        })
      }
      return t("behaviour_manage.select_positive_behaviour_placeholder", {
        positiveBehaviours: behaviourTerminology.value.positiveBehaviours
      })
    })

    const state = reactive({
      form: {
        studentIds: [],
        coreValueId: null,
        awardDescription: "",
        positiveBehaviourId: null
      },
      coreValueSelectedOption: null,
      positiveBehaviourSelectedOption: null,
      apiFieldErrors: {},
      isProcessing: false,
      isServerError: false,
      serverTitle: "",
      serverMessage: "",
      touched: false
    })

    const clientErrors = computed(() => {
      if (!state.touched) return {}
      const out = {}
      const bt = behaviourTerminology.value
      if (!normalizeBehaviourStudentIds(state.form.studentIds).length) {
        out.studentIds = t("behaviour_manage.error_select_students")
      }
      if (!state.form.coreValueId) {
        out.coreValueId = t("behaviour_manage.error_required_field", {
          field: bt.coreValue
        })
      }
      if (!state.form.positiveBehaviourId) {
        out.positiveBehaviourId = t("behaviour_manage.error_required_field", {
          field: bt.positiveBehaviours
        })
      }
      return out
    })

    const errors = computed(() => ({
      ...clientErrors.value,
      ...state.apiFieldErrors
    }))

    const patchForm = (key, value) => {
      state.form[key] = value ?? (key === "studentIds" ? [] : "")
      if (state.apiFieldErrors[key]) {
        const next = { ...state.apiFieldErrors }
        delete next[key]
        state.apiFieldErrors = next
      }
      state.isServerError = false
      state.serverTitle = ""
      state.serverMessage = ""

      if (key === "coreValueId") {
        state.form.positiveBehaviourId = null
        state.positiveBehaviourSelectedOption = null
      }
    }

    const onCoreValueSelected = (option) => {
      state.coreValueSelectedOption = option ? { ...option } : null
    }

    const onPositiveBehaviourSelected = (option) => {
      state.positiveBehaviourSelectedOption = option ? { ...option } : null
    }

    const onCancel = () => {
      emit("cancel")
    }

    const onSubmit = async () => {
      state.touched = true
      state.apiFieldErrors = {}
      state.isServerError = false
      state.serverTitle = ""
      state.serverMessage = ""

      if (Object.keys(clientErrors.value).length) return

      try {
        state.isProcessing = true
        const result = await store.dispatch(
          "behaviourManage/createPointsAward",
          state.form
        )
        modal?.setAction?.("pointHistoryAwarded", {
          ...state.form,
          ...result
        })
        emit("cancel")
      } catch (error) {
        const parsed = parseBehaviourPointsApiError(error, "award")
        if (Object.keys(parsed.fieldErrors).length) {
          state.apiFieldErrors = parsed.fieldErrors
        }

        const pointsTerm = behaviourTerminology.value.points
        state.isServerError = true
        state.serverTitle = t("behaviour_manage.error_title")
        state.serverMessage =
          parsed.message ||
          t("behaviour_manage.award_error", { points: pointsTerm })
      } finally {
        state.isProcessing = false
      }
    }

    return {
      behaviourTerminology,
      fieldLabels,
      positiveBehaviourPlaceholder,
      state,
      errors,
      patchForm,
      onCoreValueSelected,
      onPositiveBehaviourSelected,
      onCancel,
      onSubmit
    }
  }
}
</script>
